import { uniqueId } from "lodash";
import { Component, Fragment } from "react";
import BulkProjectEditChange from "@app/projects/edit/bulkProjectEditChange";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import Project from "@app/projects/project";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { ProviderSet } from "@app/provider-sets";
import { validateProviderSets } from "@app/projects/network-assessment/ValidateProviderSets";

interface ProviderOptionProps extends FeatureFlagContextProps {
  checkboxClass: string;
  disabled: boolean;
  marketProviderSets: ProviderSet[];
  onCheckBoxChange: (property: UpdateResourceKeys) => void;
  onMarketChanged: (value: string) => void;
  onProspectsChanged: (value: string) => void;
  onProvidersChanged: (value: string) => void;
  providerSets: ProviderSet[];
  project: Project;
  shared: boolean;
  networkDisabled: boolean;
  hideUseInMarketAnalysis: boolean;
  hideUseMarketProvidersForImpact: boolean;
  onChangeNetworkAlias: (alias: string) => void;
  onChangeProspectAlias: (alias: string) => void;
  onChangeMarketAlias: (alias: string) => void;
  isZipProject: boolean;
  onChangeNcpAlias: (alias: string) => void;
}

type UpdateResourceKeys = keyof Project | keyof BulkProjectEditChange;

class ProviderOptions extends Component<ProviderOptionProps> {

  public render() {
    const networkCheckboxIsDisabled = this.props.disabled || this.props.project.networkProviderSetId === null;
    const prospectCheckboxIsDisabled = this.props.disabled || this.props.project.prospectProviderSetId === null;

    return (
      <Fragment>
        <div className="tab-contents col-12">
          <div className="row">
            <div className="col-11 d-flex flex-wrap offset-1">
              <div className="col-5 label-s">Provider Set</div>
              {!this.props.isZipProject && <div className="col-4 label-s">Alias</div>}
            </div>
          </div>
          <div className="form-group row">
            <div className="col-1 align-items-center d-flex label-s">Network</div>
            <div className="col-11 d-flex">
              {this.renderNetworkProviders()}
              {(!this.props.isZipProject || this.props.enabledFeatureFlags?.has(Features.ZIP_PROSPECT_AND_MARKET)) && (
                <QESTextInput
                  containerClassName="col-4"
                  label={"Alias"}
                  value={this.props.project.networkProviderSetAlias || ""}
                  onChange={(e) => { this.props.onChangeNetworkAlias(e.target.value) }}
                  testId={"edit-providers-options__edit-project-network-alias"}
                  disabled={this.props.disabled || !this.props.project.networkProviderSetId}
                  hideLabel={true}
                  maxLength={255}
                />
              )}
            </div>

            {!this.props.hideUseInMarketAnalysis && (!this.props.isZipProject || this.props.enabledFeatureFlags?.has(Features.ZIP_PROSPECT_AND_MARKET)) &&
              this.renderCheckbox(
                "Use Network Providers for Market Analysis",
                !networkCheckboxIsDisabled && this.props.project.useNetworkProvidersForMarketAnalysis,
                "useNetworkProvidersForMarketAnalysis",
                (this.props.disabled || this.props.project.networkProviderSetId === null) ? "checkbox disabled" : "checkbox",
                "col-5 d-inline-flex offset-1 ps-2",
                networkCheckboxIsDisabled)}
          </div>

          {(!this.props.isZipProject || this.props.enabledFeatureFlags?.has(Features.ZIP_PROSPECT_AND_MARKET)) && (
            <>
              <div className="form-group row">
                <div className="col-1 align-items-center d-flex label-s">Prospect</div>
                <div className="col-11 d-flex">
                  {this.renderProspectProviders()}
                  <QESTextInput
                    containerClassName="col-4"
                    label={"Alias"}
                    value={this.props.project.prospectProviderSetAlias || ""}
                    onChange={(e) => { this.props.onChangeProspectAlias(e.target.value) }}
                    testId={"edit-providers-options__edit-project-prospect-alias"}
                    disabled={this.props.disabled || !this.props.project.prospectProviderSetId}
                    hideLabel={true}
                    maxLength={255}
                  />
                </div>
                {!this.props.hideUseInMarketAnalysis &&
                  this.renderCheckbox(
                    "Use Prospect Providers for Market Analysis",
                    !prospectCheckboxIsDisabled && this.props.project.useProspectProvidersForMarketAnalysis,
                    "useProspectProvidersForMarketAnalysis",
                    (this.props.disabled || this.props.project.prospectProviderSetId === null) ? "checkbox disabled" : "checkbox",
                    "col-5 d-inline-flex offset-1 ps-2",
                    prospectCheckboxIsDisabled)}
              </div>

              <div className="form-group row">
                <div className="col-1 align-items-center d-flex label-s">Market</div>
                <div className="col-11 d-flex">
                  {this.renderMarketProviders()}
                  <QESTextInput
                    containerClassName="col-4"
                    label={"Alias"}
                    value={this.props.project.marketProviderSetAlias || ""}
                    onChange={(e) => { this.props.onChangeMarketAlias(e.target.value) }}
                    testId={"edit-providers-options__edit-project-market-alias"}
                    disabled={this.props.disabled || !this.props.project.marketProviderSetId}
                    hideLabel={true}
                    maxLength={255}
                  />
                </div>
              </div>

              <div className="form-group row">
                <div className="col-1 align-items-center d-flex label-s text-nowrap">Next Closest {this.props.isZipProject ? "Providers" : "Network Providers"}</div>
                <div className="col-11 d-flex flex-wrap">
                  <QESTextInput
                    containerClassName="col-4 offset-5"
                    label={"Alias"}
                    value={this.props.project.nextClosestProviderSetAlias || ""}
                    onChange={(e) => { this.props.onChangeNcpAlias(e.target.value) }}
                    testId={`edit-providers-options__ncp-alias`}
                    disabled={this.props.disabled}
                    hideLabel={true}
                    maxLength={255}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </Fragment>
    );
  }

  private renderNetworkProviders = () => {
    if (this.props.shared) {
      return (
        <QESTextInput
          label="Network"
          containerClassName="col-5 pe-3"
          disabled={true}
          value={this.props.project.providerNetworkName || ""}
          testId="edit-providers-options__edit-project-network-text-box"
          hideLabel={true}
        />
      );
    } else {
      return (
        <QESSelect
          label="Network"
          containerClassName="col-5 pe-3"
          disabled={this.props.disabled || this.props.networkDisabled}
          value={this.props.project.networkProviderSetId ? this.props.project.networkProviderSetId : ""}
          onChange={(e) => this.props.onProvidersChanged(e.target.value)}
          testId="edit-providers-options__edit-project-network-select"
          descriptiveText={this.props.networkDisabled ? "Network Providers are disabled due to at least 1 project having ProviderSetBinding." : null}
          hideLabel={true}
        >
          <option key="unselected" value="" />
          {this.props.project.networkProviderSetId == -1 && <option key="-1" value="-1">Different Network Providers Being Used</option>}
          {
            !validateProviderSets([this.props.project.networkProviderSetId], [...this.props.providerSets, ...this.props.marketProviderSets]) &&
            <option key={this.props.project.networkProviderSetId} value={this.props.project.networkProviderSetId}>Invalid Provider Set</option>
          }
          {this.props.providerSets.sort((a, b) => a.hasActivatedVersions > b.hasActivatedVersions ? -1 : 1).map((providerSet) => (
            providerSet.hasActivatedVersions ?
              <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId}>{providerSet.resource.name}</option> :
              <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId} disabled={true}>{providerSet.resource.name} - No Activated Versions</option>
          ))}
        </QESSelect>
      );
    }
  }

  private renderProspectProviders = () => {
    if (this.props.shared) {
      return (
        <QESTextInput
          label="Prospect"
          containerClassName="col-5 pe-3"
          disabled={true}
          value={this.props.project.prospectNetworkName || ""}
          testId="edit-providers-options__edit-project-prospect-text-box"
          hideLabel={true}
        />
      );
    } else {
      return (
        <QESSelect
          label="Prospect"
          containerClassName="col-5 pe-3"
          disabled={this.props.disabled}
          value={this.props.project.prospectProviderSetId ? this.props.project.prospectProviderSetId : ""}
          onChange={(e) => this.props.onProspectsChanged(e.target.value)}
          testId="edit-providers-options__edit-project-prospect-select"
          hideLabel={true}
        >
          <option key="unselected" value="" />
          {this.props.project.prospectProviderSetId == -1 && <option key="-1" value="-1">Different Prospect Providers Being Used</option>}
          {
            !validateProviderSets([this.props.project.prospectProviderSetId], [...this.props.providerSets, ...this.props.marketProviderSets]) &&
            <option key={this.props.project.prospectProviderSetId} value={this.props.project.prospectProviderSetId}>Invalid Provider Set</option>
          }
          {this.props.providerSets.sort((a, b) => a.hasActivatedVersions > b.hasActivatedVersions ? -1 : 1).map((providerSet) => (
            providerSet.hasActivatedVersions ?
              <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId}>{providerSet.resource.name}</option> :
              <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId} disabled={true}>{providerSet.resource.name} - No Activated Versions</option>
          ))}
        </QESSelect>
      );
    }
  }

  private renderMarketProviders = () => {
    if (this.props.shared) {
      return (
        <QESTextInput
          label="Market"
          containerClassName="col-5 pe-3"
          disabled={true}
          value={this.props.project.marketNetworkName || ""}
          testId="edit-providers-options__edit-project-market-text-box"
          hideLabel={true}
        />
      );
    } else {
      return (
        <QESSelect
          label="Market"
          containerClassName="col-5 pe-3"
          disabled={this.props.disabled}
          value={this.props.project.marketProviderSetId ? this.props.project.marketProviderSetId : ""}
          onChange={(e) => this.props.onMarketChanged(e.target.value)}
          testId="edit-providers-options__edit-project-market-select"
          hideLabel={true}
        >
          <option key="unselected" value="" />
          {this.props.project.marketProviderSetId == -1 && <option key="-1" value="-1">Different Market Providers Being Used</option>}
          {
            !validateProviderSets([this.props.project.marketProviderSetId], [...this.props.providerSets, ...this.props.marketProviderSets]) &&
            <option key={this.props.project.marketProviderSetId} value={this.props.project.marketProviderSetId}>Invalid Provider Set</option>
          }
          <optgroup label="System Networks">
            {this.props.marketProviderSets.sort((a, b) => a.hasActivatedVersions > b.hasActivatedVersions ? -1 : 1).map((marketProviderSet) => (
              marketProviderSet.hasActivatedVersions ?
                <option key={marketProviderSet.resource.resourceId} value={marketProviderSet.resource.resourceId}>{marketProviderSet.resource.name}</option> :
                <option key={marketProviderSet.resource.resourceId} value={marketProviderSet.resource.resourceId} disabled={true}>{marketProviderSet.resource.name} - No Activated Versions</option>
            ))}
          </optgroup>
          <optgroup label="Client Networks">
            {this.props.providerSets.sort((a, b) => a.hasActivatedVersions > b.hasActivatedVersions ? -1 : 1).map((providerSet) => (
              providerSet.hasActivatedVersions ?
                <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId}>{providerSet.resource.name}</option> :
                <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId} disabled={true}>{providerSet.resource.name} - No Activated Versions</option>
            ))}
          </optgroup>
        </QESSelect>
      );
    }
  }

  private renderCheckbox(
    checkboxText: string,
    checkboxValue: boolean,
    eventProperty: UpdateResourceKeys,
    checkboxCssClass: string,
    outerDivCssClass: string,
    disabled: boolean,
    tipText?: string,
  ) {

    if (this.props.shared && !checkboxValue) {
      //only display checked checkboxes for shared users
      return null;
    }

    const inputId = uniqueId();
    return (
      <div className={outerDivCssClass} key={checkboxText}>
        <TooltipWrapper text={tipText}>
          <QESCheckbox
            containerClassName={`${checkboxCssClass} form-check`}
            label={
              <span style={{ color: checkboxValue == undefined ? "red" : "inherit" }}>{checkboxText}</span>
            }
            id={inputId}
            className="custom-control-inline"
            indeterminate={checkboxValue === undefined}
            checked={checkboxValue}
            onChange={() => { this.props.onCheckBoxChange(eventProperty); }}
            disabled={disabled}
            testId="edit-providers-options__edit-project-Use-market-for-impact-checkbox"
          />
        </TooltipWrapper>
      </div >
    );
  }
}

export default withFeatureFlagContext(ProviderOptions);
